import { useState } from "react";
import { api, TELEGRAM_URL, PHONE, PHONE_TEL } from "../lib/config";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Send, Phone, MessageCircle } from "lucide-react";
import { toast } from "sonner";

export default function ContactForm() {
    const [form, setForm] = useState({
        name: "",
        phone: "",
        email: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.phone || !form.message) {
            toast.error("Name, phone and message are required.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/contact", form);
            toast.success("Message received. We'll cruise back soon.");
            setForm({ name: "", phone: "", email: "", message: "" });
        } catch (err) {
            toast.error("Couldn't send. Hit us on Telegram instead.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            data-testid="contact-section"
            id="contact"
            className="relative py-20 sm:py-28 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
        >
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
                {/* Left — info */}
                <div>
                    <p className="font-mono text-xs uppercase tracking-[0.3em] text-highway-sunset mb-3">
                        // The Pit Stop
                    </p>
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tighter text-balance">
                        Roll the window down.{" "}
                        <span className="italic font-light text-white/80">
                            Talk to us.
                        </span>
                    </h2>
                    <p className="mt-4 text-white/65 text-lg max-w-md">
                        Telegram is fastest. Phone works too. Or use the form —
                        we read every message that comes through.
                    </p>

                    <div className="mt-10 space-y-3">
                        <a
                            data-testid="contact-telegram"
                            href={TELEGRAM_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-4 p-5 rounded-2xl bg-card hover:bg-card/70 border border-border hover:border-highway-telegram/50 transition-all group"
                        >
                            <div className="w-12 h-12 rounded-xl bg-highway-telegram/15 border border-highway-telegram/30 flex items-center justify-center">
                                <Send className="w-5 h-5 text-highway-telegram" />
                            </div>
                            <div className="flex-1">
                                <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                                    Telegram
                                </p>
                                <p className="font-display font-semibold text-white">
                                    @ Order on the channel
                                </p>
                            </div>
                            <span className="text-white/30 group-hover:text-white transition-colors">
                                →
                            </span>
                        </a>

                        <a
                            data-testid="contact-phone"
                            href={`tel:${PHONE_TEL}`}
                            className="flex items-center gap-4 p-5 rounded-2xl bg-card hover:bg-card/70 border border-border hover:border-highway-red/50 transition-all group"
                        >
                            <div className="w-12 h-12 rounded-xl bg-highway-red/15 border border-highway-red/30 flex items-center justify-center">
                                <Phone className="w-5 h-5 text-highway-red" />
                            </div>
                            <div className="flex-1">
                                <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                                    Phone
                                </p>
                                <p className="font-display font-semibold text-white font-mono">
                                    {PHONE}
                                </p>
                            </div>
                            <span className="text-white/30 group-hover:text-white transition-colors">
                                →
                            </span>
                        </a>

                        <div className="flex items-center gap-4 p-5 rounded-2xl bg-card border border-border">
                            <div className="w-12 h-12 rounded-xl bg-highway-blue/15 border border-highway-blue/30 flex items-center justify-center">
                                <MessageCircle className="w-5 h-5 text-highway-blue" />
                            </div>
                            <div className="flex-1">
                                <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                                    Service Area
                                </p>
                                <p className="font-display font-semibold text-white">
                                    South Jersey · Local Delivery Only
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right — form */}
                <form
                    data-testid="contact-form"
                    onSubmit={onSubmit}
                    className="glass rounded-3xl p-7 sm:p-9 grain relative overflow-hidden space-y-5"
                >
                    <div className="space-y-2">
                        <Label
                            htmlFor="name"
                            className="font-mono text-xs uppercase tracking-widest text-white/60"
                        >
                            Name
                        </Label>
                        <Input
                            id="name"
                            data-testid="contact-name-input"
                            value={form.name}
                            onChange={update("name")}
                            className="bg-background/60 border-border text-white h-12"
                            placeholder="Your name"
                        />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-5">
                        <div className="space-y-2">
                            <Label
                                htmlFor="phone"
                                className="font-mono text-xs uppercase tracking-widest text-white/60"
                            >
                                Phone
                            </Label>
                            <Input
                                id="phone"
                                data-testid="contact-phone-input"
                                value={form.phone}
                                onChange={update("phone")}
                                className="bg-background/60 border-border text-white h-12 font-mono"
                                placeholder="(555) 555-5555"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label
                                htmlFor="email"
                                className="font-mono text-xs uppercase tracking-widest text-white/60"
                            >
                                Email <span className="opacity-50">(opt)</span>
                            </Label>
                            <Input
                                id="email"
                                data-testid="contact-email-input"
                                type="email"
                                value={form.email}
                                onChange={update("email")}
                                className="bg-background/60 border-border text-white h-12"
                                placeholder="you@road.com"
                            />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label
                            htmlFor="message"
                            className="font-mono text-xs uppercase tracking-widest text-white/60"
                        >
                            Message
                        </Label>
                        <Textarea
                            id="message"
                            data-testid="contact-message-input"
                            value={form.message}
                            onChange={update("message")}
                            rows={5}
                            className="bg-background/60 border-border text-white"
                            placeholder="What are you looking for? Drop your zip if you want delivery."
                        />
                    </div>
                    <Button
                        data-testid="contact-submit-btn"
                        type="submit"
                        disabled={submitting}
                        className="w-full rounded-full bg-highway-red hover:bg-highway-red/90 text-white py-6 text-base font-semibold glow-red"
                    >
                        {submitting ? "Sending..." : "Send Message"}
                    </Button>
                </form>
            </div>
        </section>
    );
}
